import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CheckboxGroup, Input } from '@nextui-org/react';
import {
  getCaseReasons,
  saveCaseReason,
  type CaseReason,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert, LoadingState } from '../../components/ui';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';

interface Props {
  exporterId: number;
  identity: string;
  selected?: string[];
  onSelectionChange?: (values: string[]) => void;
  disabled?: boolean;
  onReadyChange?: (ready: boolean) => void;
  /** false: sin título propio (la sección que lo contiene ya lo muestra). */
  showTitle?: boolean;
}
export default function CaseReasons(props: Props) {
  return (
    <ReasonCatalog key={`${props.identity}:${props.exporterId}`} {...props} />
  );
}
function ReasonCatalog({
  exporterId,
  identity,
  selected,
  onSelectionChange,
  disabled,
  onReadyChange,
  showTitle = true,
}: Props) {
  const { t } = useTranslation('shipmentCases');
  const load = useCallback(
    (signal: AbortSignal) => getCaseReasons(exporterId, signal),
    [exporterId]
  );
  const request = useScopedRequest(`${identity}:reasons:${exporterId}`, load);
  const [managing, setManaging] = useState(selected === undefined);
  const [editing, setEditing] = useState<CaseReason | undefined>();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const reasons = request.data || [];
  const active = reasons.filter((r) => r.active);
  const frozen = disabled || saving;
  const ready =
    !request.loading &&
    !request.error &&
    !saving &&
    (selected || []).every((name) => active.some((r) => r.name === name));
  useEffect(() => {
    onReadyChange?.(ready);
  }, [onReadyChange, ready]);
  useEffect(() => {
    if (request.loading || request.error || !request.data || !selected) return;
    const valid = selected.filter((name) =>
      request.data!.some((r) => r.active && r.name === name)
    );
    if (valid.length !== selected.length) onSelectionChange?.(valid);
  }, [
    request.loading,
    request.error,
    request.data,
    selected,
    onSelectionChange,
  ]);
  async function save(
    nameValue: string,
    existing?: CaseReason,
    activeValue = true
  ) {
    if (pending.current || disabled) return;
    pending.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await saveCaseReason(
        exporterId,
        nameValue.trim(),
        existing,
        activeValue
      );
      if (!alive.current) return;
      if (selected && onSelectionChange) {
        const next = selected.filter((s) => s !== existing?.name);
        if (result.active && (!existing || selected.includes(existing.name)))
          next.push(result.name);
        onSelectionChange([...new Set(next)]);
      }
      setEditing(undefined);
      setName('');
      await request.reload();
    } catch (e) {
      if (alive.current) {
        setError(
          e instanceof Error
            ? e.message
            : String(
                (e as { message?: string })?.message ||
                  t('No se pudo guardar el motivo.')
              )
        );
        await request.reload();
      }
    } finally {
      pending.current = false;
      if (alive.current) setSaving(false);
    }
  }
  return (
    <section
      className='flex flex-col gap-3'
      aria-label={t('Motivos de Reclamos')}
    >
      <div
        className={`flex flex-wrap items-center gap-2 ${showTitle ? 'justify-between' : 'justify-end'}`}
      >
        {showTitle && (
          <p className='font-semibold'>{t('Motivos de Reclamos')}</p>
        )}
        {selected !== undefined && (
          <Button
            size='sm'
            variant='flat'
            isDisabled={frozen}
            onPress={() => setManaging(!managing)}
          >
            {t(managing ? 'Ocultar administración' : 'Administrar motivos')}
          </Button>
        )}
      </div>
      {request.loading ? (
        <LoadingState label={t('Cargando motivos…')} />
      ) : request.error ? (
        <Alert variant='danger'>
          {request.error}
          <Button variant='flat' onPress={() => void request.reload()}>
            {t('Reintentar')}
          </Button>
        </Alert>
      ) : selected !== undefined ? (
        <CheckboxGroup
          aria-label={t('Motivos')}
          value={selected}
          onValueChange={onSelectionChange}
          isDisabled={frozen}
          orientation={showTitle ? 'vertical' : 'horizontal'}
          classNames={showTitle ? undefined : { wrapper: 'gap-x-6 gap-y-2' }}
        >
          {active.map((r) => (
            <Checkbox key={r.id} value={r.name}>
              {r.name}
            </Checkbox>
          ))}
        </CheckboxGroup>
      ) : null}
      {!request.loading && !request.error && active.length === 0 && (
        <p className='text-sm text-default-500'>
          {t('Agrega un motivo para crear casos.')}
        </p>
      )}
      {managing && (
        <div className='flex flex-col gap-3 rounded-medium border border-default-200 p-3'>
          <p className='text-sm text-default-500'>
            {t(
              'Lista compartida por tu exportadora. Los cambios no modifican casos anteriores.'
            )}
          </p>
          <Input
            label={t(editing ? 'Editar motivo' : 'Nuevo motivo')}
            value={name}
            onValueChange={setName}
            maxLength={120}
            isDisabled={frozen}
          />
          <div className='flex flex-wrap gap-2'>
            <Button
              size='sm'
              color='primary'
              isLoading={saving}
              isDisabled={
                frozen ||
                !name.trim() ||
                request.loading ||
                Boolean(request.error)
              }
              onPress={() => void save(name, editing)}
            >
              {t(editing ? 'Guardar motivo' : 'Agregar motivo')}
            </Button>
            {editing && (
              <Button
                size='sm'
                variant='light'
                isDisabled={frozen}
                onPress={() => {
                  setEditing(undefined);
                  setName('');
                }}
              >
                {t('Cancelar')}
              </Button>
            )}
          </div>
          {reasons.map((r) => (
            <div
              key={r.id}
              className='flex flex-wrap items-center justify-between gap-2'
            >
              <span
                className={r.active ? 'text-sm' : 'text-sm text-default-500'}
              >
                {r.name}
                {!r.active && ` · ${t('Inactivo')}`}
              </span>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='light'
                  isDisabled={frozen || request.loading}
                  onPress={() => {
                    setEditing(r);
                    setName(r.name);
                  }}
                >
                  {t('Editar')}
                </Button>
                <Button
                  size='sm'
                  variant='light'
                  isDisabled={frozen || request.loading}
                  onPress={() => void save(r.name, r, !r.active)}
                >
                  {t(r.active ? 'Desactivar' : 'Activar')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <Alert variant='danger'>{error}</Alert>}
    </section>
  );
}
